/**
 * Pricing configuration - fetches from API with fallback to defaults
 */

import type { SupportedCurrency, SupportedCountry } from './config'
import { formatPrice as formatPriceUtil, getCurrencyForCountry, isMultiCurrencyEnabled, getDefaultCurrency } from './config'
import { buildApiUrl } from './api/config'

export interface PlanPricing {
  monthly: number
  yearly: number
}

export interface PlanLimits {
  audioMinutesMonthly: number
  documentsMonthly: number
  pagesMonthly: number
  storageGbMonthly: number
}

export interface Plan {
  id: string
  name: string
  description: string
  pricing: Record<SupportedCurrency, PlanPricing>
  minutesPerMonth: number
  features: string[]
  highlighted: boolean
  trialDays?: number
  limits?: PlanLimits
}

export interface ApiPlan {
  id: string
  name: string
  description: string
  currency: string
  pricing: PlanPricing
  limits: PlanLimits
  features: string[]
  trialDays: number
  highlighted: boolean
  sortOrder: number
}

// Cache for fetched plans
let cachedPlans: ApiPlan[] | null = null
let cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

/**
 * Fetch plans from API
 */
export async function fetchPlansFromApi(currency: string = 'USD'): Promise<ApiPlan[]> {
  try {
    // Check cache
    const now = Date.now()
    if (cachedPlans && now - cacheTimestamp < CACHE_TTL) {
      return cachedPlans
    }

    const response = await fetch(buildApiUrl(`/api/billing/plans?currency=${currency}`), {
      next: { revalidate: 300 }, // Cache for 5 minutes in Next.js
    })

    if (!response.ok) {
      throw new Error('Failed to fetch plans')
    }

    const data = await response.json()

    // Handle both old API format (data.plans) and new API format (array)
    if (Array.isArray(data)) {
      cachedPlans = data
      cacheTimestamp = now
      return data
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch plans')
    }

    cachedPlans = data.plans
    cacheTimestamp = now

    return data.plans
  } catch (error) {
    console.error('Error fetching plans from API:', error)
    return []
  }
}

/**
 * Default fallback plans (used when API is unavailable)
 * @deprecated Use fetchPlansFromApi or getPlansForCountryAsync instead
 */
export const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for trying out the service',
    pricing: {
      USD: { monthly: 0, yearly: 0 },
      BRL: { monthly: 0, yearly: 0 },
      EUR: { monthly: 0, yearly: 0 },
    },
    minutesPerMonth: 60,
    features: [
      '60 minutes audio/month',
      '10 documents/month',
      '10 languages',
      'Standard support',
    ],
    highlighted: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'Great for individuals',
    pricing: {
      USD: { monthly: 9.99, yearly: 99.99 },
      BRL: { monthly: 49.99, yearly: 499.99 },
      EUR: { monthly: 9.19, yearly: 91.99 },
    },
    minutesPerMonth: 300,
    features: [
      '300 minutes audio/month',
      '50 documents/month',
      '15 languages',
      'Speaker diarization',
      'Highlights & topics',
      'Priority support',
    ],
    highlighted: true,
    trialDays: 14,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For power users',
    pricing: {
      USD: { monthly: 29.99, yearly: 299.99 },
      BRL: { monthly: 149.99, yearly: 1499.99 },
      EUR: { monthly: 27.59, yearly: 275.99 },
    },
    minutesPerMonth: 1200,
    features: [
      '1200 minutes audio/month',
      '200 documents/month',
      'Unlimited languages',
      'Advanced AI summaries',
      'Custom content types',
      'Chat with transcriptions',
      'API access',
    ],
    highlighted: false,
    trialDays: 14,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    pricing: {
      USD: { monthly: 99.99, yearly: 999.99 },
      BRL: { monthly: 499.99, yearly: 4999.99 },
      EUR: { monthly: 91.99, yearly: 919.99 },
    },
    minutesPerMonth: 6000,
    features: [
      '6000 minutes audio/month',
      '1000 documents/month',
      'Dedicated support',
      'Custom integrations',
      'SLA guarantee',
      'Team management',
      'Advanced analytics',
    ],
    highlighted: false,
    trialDays: 30,
  },
]

/**
 * Get plan by ID (sync - uses cached/fallback data)
 */
export function getPlanById(planId: string): Plan | undefined {
  return plans.find((plan) => plan.id === planId)
}

/**
 * Get plan by ID from API (async)
 */
export async function getPlanByIdAsync(planId: string, currency: string = 'USD'): Promise<ApiPlan | null> {
  try {
    // Use list endpoint and filter - new API doesn't have single plan endpoint
    const plans = await fetchPlansFromApi(currency)
    return plans.find(p => p.id === planId) || null
  } catch (error) {
    console.error('Error fetching plan:', error)
    return null
  }
}

/**
 * Get the display price for a plan in a specific currency
 */
export function getPlanPrice(
  plan: Plan,
  currency: SupportedCurrency,
  billingCycle: 'monthly' | 'yearly'
): number {
  return plan.pricing[currency]?.[billingCycle] ?? plan.pricing.USD[billingCycle]
}

/**
 * Get formatted price string for display
 */
export function getFormattedPlanPrice(
  plan: Plan,
  currency: SupportedCurrency,
  billingCycle: 'monthly' | 'yearly'
): string {
  const price = getPlanPrice(plan, currency, billingCycle)
  return formatPriceUtil(price, currency)
}

/**
 * Calculate yearly savings percentage
 */
export function getYearlySavingsPercent(plan: Plan | ApiPlan, currency?: SupportedCurrency): number {
  let monthlyPrice: number
  let yearlyPrice: number

  if ('pricing' in plan && typeof plan.pricing === 'object' && 'monthly' in plan.pricing) {
    // ApiPlan format
    monthlyPrice = (plan as ApiPlan).pricing.monthly
    yearlyPrice = (plan as ApiPlan).pricing.yearly
  } else {
    // Legacy Plan format
    const legacyPlan = plan as Plan
    const curr = currency || 'USD'
    monthlyPrice = legacyPlan.pricing[curr]?.monthly ?? 0
    yearlyPrice = legacyPlan.pricing[curr]?.yearly ?? 0
  }

  if (monthlyPrice === 0) return 0

  const monthlyTotal = monthlyPrice * 12
  const savings = ((monthlyTotal - yearlyPrice) / monthlyTotal) * 100
  return Math.round(savings)
}

/**
 * Get plans for a specific country with localized pricing (sync - uses fallback data)
 * @deprecated Use getPlansForCountryAsync for API data
 */
export function getPlansForCountry(country: SupportedCountry) {
  const currency = isMultiCurrencyEnabled()
    ? getCurrencyForCountry(country)
    : getDefaultCurrency()

  return plans.map((plan) => ({
    ...plan,
    displayPricing: {
      monthly: getFormattedPlanPrice(plan, currency, 'monthly'),
      yearly: getFormattedPlanPrice(plan, currency, 'yearly'),
      monthlyAmount: getPlanPrice(plan, currency, 'monthly'),
      yearlyAmount: getPlanPrice(plan, currency, 'yearly'),
      currency,
      yearlySavings: getYearlySavingsPercent(plan, currency),
    },
  }))
}

/**
 * Get plans for a specific country from API (async)
 */
export async function getPlansForCountryAsync(country: SupportedCountry) {
  const currency = isMultiCurrencyEnabled()
    ? getCurrencyForCountry(country)
    : getDefaultCurrency()

  const apiPlans = await fetchPlansFromApi(currency)

  if (apiPlans.length === 0) {
    // Fallback to static plans
    return getPlansForCountry(country)
  }

  return apiPlans.map((plan) => {
    // Add defensive check for missing pricing
    if (!plan.pricing) {
      console.warn(`Plan ${plan.id} is missing pricing information`)
      return {
        ...plan,
        displayPricing: {
          monthly: formatPriceUtil(0, currency),
          yearly: formatPriceUtil(0, currency),
          monthlyAmount: 0,
          yearlyAmount: 0,
          currency,
          yearlySavings: 0,
        },
      }
    }

    return {
      ...plan,
      displayPricing: {
        monthly: formatPriceUtil(plan.pricing.monthly, currency),
        yearly: formatPriceUtil(plan.pricing.yearly, currency),
        monthlyAmount: plan.pricing.monthly,
        yearlyAmount: plan.pricing.yearly,
        currency,
        yearlySavings: getYearlySavingsPercent(plan),
      },
    }
  })
}

/**
 * Get plans with default currency (sync - uses fallback data)
 */
export function getPlansWithDefaultCurrency() {
  const currency = getDefaultCurrency()

  return plans.map((plan) => ({
    ...plan,
    displayPricing: {
      monthly: getFormattedPlanPrice(plan, currency, 'monthly'),
      yearly: getFormattedPlanPrice(plan, currency, 'yearly'),
      monthlyAmount: getPlanPrice(plan, currency, 'monthly'),
      yearlyAmount: getPlanPrice(plan, currency, 'yearly'),
      currency,
      yearlySavings: getYearlySavingsPercent(plan, currency),
    },
  }))
}

/**
 * Detect user's country from browser settings
 */
export function detectUserCountry(): SupportedCountry {
  if (typeof window === 'undefined') return 'OTHER'

  const language = navigator.language || (navigator as any).userLanguage || ''

  if (language.startsWith('pt-BR') || language === 'pt') {
    return 'BR'
  }

  if (language.startsWith('de') || language.startsWith('fr') ||
      language.startsWith('it') || language.startsWith('es') ||
      language.startsWith('nl') || language.startsWith('pl')) {
    return 'EU'
  }

  if (language.startsWith('en-US') || language.startsWith('en')) {
    return 'US'
  }

  return 'OTHER'
}

/**
 * Get trial information for a plan
 */
export function getTrialInfo(plan: Plan | ApiPlan): { hasTrial: boolean; trialDays: number } {
  const trialDays = plan.trialDays || 0
  return {
    hasTrial: trialDays > 0,
    trialDays,
  }
}

/**
 * Format price with currency symbol
 */
export function formatPrice(amount: number, currency: string): string {
  return formatPriceUtil(amount, currency as SupportedCurrency)
}
